<template>
  <div>
    <div
      v-if="isPreTestMastered && !tiny"
      class="col-12"
    >
      <banner
        type="success"
        show-icon
        :message="$t('run.collectData.ptpProgramMeetingCriteria')"
        class="q-mb-md"
      />
    </div>
    <div
      v-else-if="isPreTestFailed && !tiny"
      class="col-12"
    >
      <banner
        type="warning"
        show-icon
        :message="$t('run.collectData.ptpProgramNotMeetingCriteria')"
        class="q-mb-md"
      >
        <template #action>
          <q-btn
            flat
            :label="$t('run.collectData.proceedWithCriteria')"
            @click="onSwitchToTeach"
          />
        </template>
      </banner>
    </div>
    <div
      v-else-if="isTeachPhaseComplete && !tiny"
      class="col-12"
    >
      <banner
        type="success"
        show-icon
        :message="$t('run.collectData.ptpProgramTeachPhaseComplete')"
        class="q-mb-md"
      >
        <template #action>
          <q-btn
            flat
            :label="$t('run.collectData.proceedWithCriteria')"
            @click="onSwitchToPostTest"
          />
        </template>
      </banner>
    </div>
    <div
      v-else-if="isPostTestPhaseComplete && !tiny"
      class="col-12"
    >
      <banner
        type="success"
        show-icon
        :message="$t('run.collectData.ptpProgramPostTestPhaseComplete')"
        class="q-mb-md"
      />
    </div>
    <div
      :class="{
        'no-wrap': dense && tiny,
        'q-col-gutter-sm': dense && tiny,
        'q-col-gutter-md': !dense || !tiny,
      }"
      class="row"
    >
      <span
        class="items-end row"
      >
        <q-card
          class="col-auto column full-height noselect cursor-pointer"
          :class="{'bg-yellow-2': isCurrentModePreTest,
                   'bg-blue-2': isCurrentModeTeach,
                   'bg-green-2': isCurrentModePostTest,
          }"
          @click="onSwitchMode"
        >
          <q-card-section
            v-if="!tiny"
            class="col row items-center justify-center q-pb-none"
          >
            <q-badge
              floating
            >
              {{ currentModeTrials }}
            </q-badge>
            {{ currentModeLabel }}
          </q-card-section>
          <q-card-section
            class="col row items-center justify-center"
            :class="{
              'q-pt-xs': !tiny,
            }"
          >
            <q-badge
              v-if="tiny"
              floating
            >
              {{ currentModeTrials }}
            </q-badge>
            <q-chip>{{ durationFormatted }}</q-chip>
          </q-card-section>
        </q-card>
      </span>
      <span
        v-for="target in availableTargets"
        :key="target.id"
        :class="{
          'col-12': target?.dataType === 'instructions'
        } "
      >
        <target-data
          ref="targets"
          :session-program="sessionProgram"
          :target="target"
          :data="data"
          :active-prompt-levels="activePromptLevels"
          :dense="dense"
          :tiny="tiny"
          :disable="disable"
          :workflow-statuses="workflowStatuses"
          :session-paused="sessionPaused"
          :session-duration-offset="sessionDurationOffset"
          @record="onRecordData"
          @record-program-data="onRecordProgramData"
        />
      </span>
    </div>
  </div>
</template>
<script>
import TargetData from 'components/Programs/DataCollection/TargetData';
import Banner from 'components/UI/Banner';
import { Timestamp } from 'firebase/firestore';
import { chain, get, has, head, includes, isArray, isNaN, meanBy, reject } from 'lodash';
import { PTP_MODES } from 'src/constants/programs';
import DataCollectionProgram from 'src/mixins/DataCollectionProgram';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';

export default {

  name: 'PtpProgram',

  components: { TargetData, Banner },

  mixins: [DataCollectionProgram, Interactions, Targets, Toolbox],

  emits: ['record-program-data', 'record'],

  data() {
    return {
      modes: [PTP_MODES.PRE_TEST, PTP_MODES.TEACH, PTP_MODES.POST_TEST],

      defaultTeachDuration: 600,
      defaultNumTestTrials: 10,

      timerStart: null,
      timerInterval: null,

      duration: 0,
      masteringCriteria: 0.8,
    };
  },

  computed: {

    isCurrentModePreTest() {
      return this.currentMode === PTP_MODES.PRE_TEST;
    },

    isCurrentModeTeach() {
      return this.currentMode === PTP_MODES.TEACH;
    },

    isCurrentModePostTest() {
      return this.currentMode === PTP_MODES.POST_TEST;
    },

    teachPhaseDuration() {
      return this.sessionProgram?.program?.ptpTeachPhaseDuration ?? this.defaultTeachDuration;
    }, // teachPhaseDuration()

    numTestTrials() {
      const raw = this.sessionProgram?.program?.ptpNumTestTrials;
      const normalized = Number(raw);
      return Number.isFinite(normalized) && normalized > 0
        ? Math.floor(normalized)
        : this.defaultNumTestTrials;
    }, // numTestTrials()

    currentModeTrials() {
      if (this.currentMode === PTP_MODES.TEACH) {
        return this.formatDuration(this.teachPhaseDuration, false);
      }
      if ([PTP_MODES.PRE_TEST, PTP_MODES.POST_TEST].includes(this.currentMode)) {
        const currentModeTrials = chain(this.data)
          .get('data')
          .filter({ mode: this.currentMode })
          .map('trial')
          .uniq()
          .size()
          .value();
        return this.$t(
          'run.collectData.textCurrentModeTrials',
          { currentModeTrials, numTestTrials: this.numTestTrials },
        );
      }
      return '';
    }, // currentModeTrials()

    isPreTestMastered() {
      const preTestTrials = chain(this.data)
        .get('data')
        .filter({ mode: PTP_MODES.PRE_TEST })
        .filter('trial')
        .uniqBy('trial')
        .size()
        .value();

      if (preTestTrials < this.numTestTrials) {
        return false;
      }
      const preTestScore = chain(this.data)
        .get('data')
        .filter((dataItem) => (
          dataItem.mode === PTP_MODES.PRE_TEST
          && dataItem.trial
          && has(dataItem, 'promptLevel')
        ))

        // In case we have multiple observations for the same trial (rate, task analysis, etc),
        // we need to calculate the average score for that trial:
        .groupBy('trial')
        .map((trials) => (meanBy(trials, 'scoreValue')))
        .reject((meanScore) => isNaN(meanScore))
        // ---

        .sum()
        .value();

      return (preTestScore / preTestTrials) / 100 >= this.masteringCriteria;
    }, // isPreTestMastered()

    isPreTestFailed() {
      if (this.currentMode !== PTP_MODES.PRE_TEST) {
        return false;
      }
      const preTestTrials = chain(this.data)
        .get('data')
        .filter({ mode: PTP_MODES.PRE_TEST })
        .filter('trial')
        .uniqBy('trial')
        .size()
        .value();

      if (preTestTrials < this.numTestTrials) {
        return false;
      }
      const preTestScore = chain(this.data)
        .get('data')
        .filter((dataItem) => (
          dataItem.mode === PTP_MODES.PRE_TEST
          && dataItem.trial
          && has(dataItem, 'promptLevel')
        ))

        // In case we have multiple observations for the same trial (rate, task analysis, etc),
        // we need to calculate the average score for that trial:
        .groupBy('trial')
        .map((trials) => (meanBy(trials, 'scoreValue')))
        .reject((meanScore) => isNaN(meanScore))
        // ---

        .sum()
        .value();

      return (preTestScore / preTestTrials) / 100 < this.masteringCriteria;
    }, // isPreTestFailed()

    isTeachPhaseComplete() {
      return this.currentMode === PTP_MODES.TEACH
        && this.currentDuration >= this.teachPhaseDuration;
    }, // isTeachPhaseComplete()

    isPostTestPhaseComplete() {
      if (this.currentMode !== PTP_MODES.POST_TEST) {
        return false;
      }
      const preTestTrials = chain(this.data)
        .get('data')
        .filter({ mode: PTP_MODES.POST_TEST })
        .filter('trial')
        .uniqBy('trial')
        .size()
        .value();

      return preTestTrials >= this.numTestTrials;
    }, // isPostTestPhaseComplete()

    currentMode() {
      return get(this.data, 'currentMode', null);
    },

    currentModeLabel() {
      if (this.currentMode === 'null') {
        return '';
      }
      return get(this.$tm('run.collectData.probeTeachProbeModes'), this.currentMode, '');
    },

    availableTargets() {
      if (this.currentMode === PTP_MODES.TEACH) {
        if (!this.sessionProgram?.program?.alternativePromptLevelTemplate) {
          return this.sortedTargets;
        }
        // In teach mode we allow using an alternative prompt level template
        return this.sortedTargets?.map(
          (target) => (
            this.assignPromptLevelTemplate(
              target,
              this.sessionProgram?.program?.alternativePromptLevelTemplate,
            )
          ),
        );
      }
      return reject(this.sortedTargets, { dataType: 'instructions' });
    },

    elapsedDuration() {
      return get(this.data, `modeDuration_${this.currentMode}`) || 0;
    },

    currentDuration() {
      return this.elapsedDuration + this.duration;
    },

    durationFormatted() {
      if (this.currentDuration === 0) {
        return '-';
      }
      return this.formatDuration(this.currentDuration, false);
    }, // durationFormatted()

    isTimerStarted() {
      return (this.timerInterval !== null);
    }, // isTimerStarted()

  },

  created() {
    if (includes(this.modes, this.currentMode) && this.data[`modeTimerOffset_${this.currentMode}`]) {
      const timerOffset = this.data[`modeTimerOffset_${this.currentMode}`];
      if (timerOffset instanceof Timestamp) {
        this.startTimer(timerOffset.toDate());
      }
    } else {
      this.switchMode(head(this.modes), true);
    }
  },

  beforeUnmount() {
    clearInterval(this.timerInterval);
  }, // beforeUnmount()

  methods: {

    /**
       * Called by parent to signal session finish
       */
    finishSession() {
      if (this.isTimerStarted) {
        this.stopTimer();
      }
      const finalData = chain(this.$refs)
        .get('targets')
        .map((targetRef) => {
          if (chain(targetRef).get('finishSession').isFunction().value()) {
            return targetRef.finishSession();
          }
          return null;
        })
        .flatten()
        .compact()
        .map((trialData) => ({
          ...trialData,
          mode: this.currentMode,
        }))
        .value();

      return {
        finalData,
      };
    }, // finishSession()

    onRecordData(sessionProgram, target, data) {
      const saveData = { ...data, mode: this.currentMode };

      this.$emit('record', sessionProgram, target, saveData);
    }, // onRecordData()

    onRecordProgramData(sessionProgram, dataObj) {
      this.$emit('record-program-data', sessionProgram, dataObj);
    },

    onSwitchToTeach() {
      this.switchMode(PTP_MODES.TEACH);
    }, // onSwitchToTeach()

    onSwitchToPostTest() {
      this.switchMode(PTP_MODES.POST_TEST);
    }, // onSwitchToPostTest()

    onSwitchMode() {
      const modeIndex = this.modes.indexOf(this.currentMode);
      this.switchMode(this.modes[(modeIndex + 1) % this.modes.length]);
    },

    switchMode(newMode) {
      const modeTimerOffset = new Date();
      const programData = {
        currentMode: newMode,
        [`modeTimerOffset_${newMode}`]: modeTimerOffset,
      };
      if (this.currentMode) {
        programData[`modeDuration_${this.currentMode}`] = this.currentDuration;
        programData[`modeTimerOffset_${this.currentMode}`] = null;
      }
      this.startTimer(modeTimerOffset, true);
      this.$emit('record-program-data', this.sessionProgram, programData);
    }, // switchMode()

    startTimer(timeOffset = null, allowRestart = false) {
      if (this.isTimerStarted && !allowRestart) {
        // Prevent multiple simultanous timers
        return;
      }

      this.timerStart = timeOffset || new Date();

      const initTime = new Date();
      this.duration = (initTime.getTime() - this.timerStart.getTime()) / 1000;

      if (this.isTimerStarted) {
        return;
      }

      this.timerInterval = setInterval(() => {
        const curTime = new Date();
        this.duration = (curTime.getTime() - this.timerStart.getTime()) / 1000;
      }, 50);
    }, // startTimer()

    stopTimer() {
      if (!this.isTimerStarted) {
        return;
      }

      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.timerStart = null;

      this.duration = 0;
    }, // stopTimer()

    assignPromptLevelTemplate(target, promptLevelTemplate) {
      const newTarget = {
        ...target,
        promptLevelTemplate,
      };

      if (isArray(target.subTargets) && target.subTargets.length > 0) {
        newTarget.subTargets = target.subTargets.map(
          (subTarget) => this.assignPromptLevelTemplate(subTarget, promptLevelTemplate),
        );
      }
      return newTarget;
    }, // assignPromptLevelTemplate()

  },
};
</script>
